import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn, coincide } from '@/lib/cn';

export interface OpcionBuscable {
  valor: string;
  texto: string;
  /** Segunda línea: dirección, categoría, lo que desempate dos nombres parecidos. */
  detalle?: string;
}

/**
 * Un desplegable que se busca escribiendo.
 *
 * Con seis opciones un `<select>` nativo va perfecto. Con sesenta deja de
 * servir: hay que recorrer la lista con la vista y el navegador solo salta por
 * la primera letra, que con «Panadería La 33» y «Panadería El Trigal» no
 * distingue nada.
 *
 * La búsqueda ignora tildes y mayúsculas, y acepta palabras sueltas: «paisa
 * laureles» encuentra «Sabor Paisa» si su dirección está en Laureles. Es como
 * busca la gente cuando recuerda dos datos y ninguno completo.
 */
export function SelectorBuscable({
  opciones,
  valor,
  onCambio,
  placeholder = 'Elige una opción',
  error,
  id,
}: {
  opciones: OpcionBuscable[];
  valor: string;
  onCambio: (valor: string) => void;
  placeholder?: string;
  error?: boolean;
  id?: string;
}) {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [resaltado, setResaltado] = useState(0);
  const contenedor = useRef<HTMLDivElement>(null);
  const entrada = useRef<HTMLInputElement>(null);
  const lista = useRef<HTMLUListElement>(null);

  const elegida = opciones.find((o) => o.valor === valor);

  const filtradas = useMemo(
    () => opciones.filter((o) => coincide(busqueda, o.texto, o.detalle)),
    [opciones, busqueda],
  );

  // Al abrir, el cursor va al buscador: se abre para escribir, no para mirar.
  useEffect(() => {
    if (abierto) {
      entrada.current?.focus();
      setResaltado(0);
    } else {
      setBusqueda('');
    }
  }, [abierto]);

  useEffect(() => {
    if (!abierto) return;

    const fuera = (e: MouseEvent): void => {
      if (!contenedor.current?.contains(e.target as Node)) setAbierto(false);
    };

    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  /**
   * La opción resaltada se mantiene a la vista.
   *
   * Sin esto, bajar con las flechas en una lista larga mueve un resaltado que
   * ya no se ve: se sigue navegando a ciegas, que es justo lo que el teclado
   * venía a evitar.
   */
  useEffect(() => {
    if (!abierto) return;
    lista.current?.children[resaltado]?.scrollIntoView({ block: 'nearest' });
  }, [resaltado, abierto]);

  const elegir = (v: string): void => {
    onCambio(v);
    setAbierto(false);
  };

  const teclado = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setResaltado((r) => Math.min(filtradas.length - 1, r + 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setResaltado((r) => Math.max(0, r - 1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const opcion = filtradas[resaltado];
      if (opcion) elegir(opcion.valor);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setAbierto(false);
    }
  };

  return (
    <div ref={contenedor} className="relative">
      <button
        type="button"
        id={id}
        onClick={() => setAbierto((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={abierto}
        className={cn(
          'flex w-full items-center gap-2 rounded-kick border bg-white px-3.5 py-2.5 text-left outline-none transition',
          'focus:border-kick-red focus:ring-2 focus:ring-kick-red/20',
          error ? 'border-red-400' : 'border-kick-gray/30',
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate', !elegida && 'text-kick-gray')}>
          {elegida?.texto ?? placeholder}
        </span>

        {elegida && (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Quitar la selección"
            onClick={(e) => {
              // Sin esto, limpiar también abriría el desplegable.
              e.stopPropagation();
              onCambio('');
            }}
            className="shrink-0 rounded p-0.5 text-kick-gray transition hover:text-kick-red"
          >
            <X className="h-4 w-4" />
          </span>
        )}

        <ChevronDown
          aria-hidden
          className={cn('h-4 w-4 shrink-0 text-kick-gray transition', abierto && 'rotate-180')}
        />
      </button>

      {abierto && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-kick border border-kick-gray/20 bg-white shadow-xl">
          <div className="relative border-b border-kick-gray/15">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-kick-gray"
            />
            <input
              ref={entrada}
              value={busqueda}
              onChange={(e) => {
                setBusqueda(e.target.value);
                setResaltado(0);
              }}
              onKeyDown={teclado}
              placeholder="Escribe para buscar…"
              aria-label="Buscar"
              className="w-full py-2.5 pl-9 pr-3 text-sm text-kick-ink outline-none"
            />
          </div>

          {filtradas.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-kick-gray">
              Nada con «{busqueda.trim()}».
            </p>
          ) : (
            <ul ref={lista} role="listbox" className="max-h-64 overflow-y-auto py-1">
              {filtradas.map((opcion, i) => {
                const seleccionada = opcion.valor === valor;

                return (
                  <li
                    key={opcion.valor}
                    role="option"
                    aria-selected={seleccionada}
                    onMouseEnter={() => setResaltado(i)}
                    onClick={() => elegir(opcion.valor)}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 px-4 py-2.5 text-sm',
                      i === resaltado && 'bg-kick-light',
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-kick-ink">{opcion.texto}</span>
                      {opcion.detalle && (
                        <span className="block truncate text-xs text-kick-gray">
                          {opcion.detalle}
                        </span>
                      )}
                    </span>
                    {seleccionada && <Check className="h-4 w-4 shrink-0 text-kick-red" />}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
